/**
 * WHEN AN IPHONE CLOSES THE HUB'S DATABASE: ONE GUARDED RELOAD (packet B815).
 *
 * THE PROBLEM, from Sentry (docs/handoff/SENTRY-ERRORS-2026-10-13.md, item 1:
 * JAVASCRIPT-REACT-T and the b815 family, about 20 people, every one Mobile
 * Safari, all unhandled). iOS closes the IndexedDB connection under a Hub
 * that sat in the background ("The database connection is closing").
 * Firestore keeps its offline copy in IndexedDB (lib/firebase.ts,
 * initFirestoreWithCache), and when one of its own steps fails that way the
 * SDK marks its internal work queue failed FOR GOOD. Read in the installed
 * SDK (@firebase/firestore 4.15.0, AsyncQueueImpl): every later Firestore
 * call first checks that queue and throws
 *   FIRESTORE (12.14.0) INTERNAL ASSERTION FAILED: Unexpected state (ID: b815)
 * because 0xb815 is that check's id. Listeners stop updating, reads never
 * come back, and a page opened from an alert comes up empty (feedback
 * r2iDin5L, "alerts open an empty page"). Nothing short of a reload gives
 * that Firestore instance back, and before this nothing in hub/src reloaded.
 *
 * WHAT THIS FILE IS: the decision, with no import from the browser, React,
 * Firebase or Sentry, so tests/firestore-closed-recovery.test.mjs runs it
 * directly. What feeds it (Firestore's own error log, the window error and
 * unhandledrejection events, the return to the app) and what a reload and a
 * report are live in lib/firestoreRecovery.ts; the bar is
 * components/FirestoreRecoveryBar.tsx.
 *
 * A THIRD TRIGGER, 'stalled' (2026-11-01). A Firestore can also die without
 * throwing anything at all: Bobbie's Understood/complete sat on "Saving..."
 * for minutes on her iPhone and nothing reached this file, because nothing
 * here can hear a call that simply never comes back. lib/firestoreLiveness.ts
 * proves that death on the phone itself, never from slowness, and hands it in
 * through stalled(). From there it is exactly the same decision, with every
 * rule below unchanged.
 *
 * THE RULES, each one a way a reload can do harm:
 *   - Matched NARROWLY: an "INTERNAL ASSERTION FAILED" that carries "(ID:
 *     b815)", or "The database connection is closing". Nothing else. A
 *     permission error, an offline or network error, and every other
 *     Firestore assertion id leave this alone. A stall counts only once
 *     lib/firestoreLiveness.ts has proven it locally; a slow or offline
 *     network never does.
 *   - ONE automatic reload per tab every FIRESTORE_RECOVERY_WINDOW_MS (ten
 *     minutes), stamped in sessionStorage BEFORE the reload and read back.
 *     A second detection inside the window never reloads; it shows the bar.
 *     That is the loop guard, and a reload that does not cure the phone
 *     costs one reload, not a loop.
 *   - Storage that throws, or a stamp that does not stick, means NO
 *     automatic reload at all, ever: every anti-loop guard lives in storage
 *     (the lesson reloadGuard.ts records, where a throwing sessionStorage
 *     once produced an unbounded reload loop).
 *   - Never inside the shared one-minute cooldown every programmatic reload
 *     stamps (reloadGuard.ts), so this cannot take turns with the version
 *     auto-update or land on top of a sign-out reload.
 *   - Never while an upload is in flight (unsavedWork.ts's in-flight count,
 *     which every Storage write and every gateway upload holds), never over
 *     unsaved words or an open dialog (the same signals the version
 *     auto-update uses), and only while the page is on screen.
 *   - When it is not safe YET, the bar "The Hub lost its connection. Tap to
 *     reload." shows, and the decision is asked again at the next return to
 *     the app (the page becoming visible, or a restore from the back/forward
 *     cache). Like the version auto-update, it never decides on a timer, so
 *     it cannot reload out from under somebody who is reading.
 *   - A tap on the bar always reloads: that is a person choosing to.
 *   - A reload that did not go never leaves the page stuck. A page can warn
 *     before it is left, and on a desktop the person can answer Stay; then
 *     the page is still here and its database is still dead. The bar shows
 *     at once when the warning is seen holding the reload up, and in every
 *     other case the next return to the app decides again (review of #1393,
 *     2026-10-20: the "reloading" flag used to stay set for good, so the bar
 *     never showed and only a manual refresh brought the page back).
 */

// Explicit .ts extension (allowImportingTsExtensions in tsconfig.json): this
// file is imported directly by node:test under --experimental-strip-types,
// which will not resolve an extensionless relative specifier. unsavedWork.ts
// imports nothing, so this file stays runnable outside the browser.
import { RELOAD_COOLDOWN_MS } from './unsavedWork.ts';

/** The bar's words, exactly as the packet gave them. The whole bar is the button. */
export const FIRESTORE_RECOVERY_BAR_TEXT = 'The Hub lost its connection. Tap to reload.';

/** At most one automatic reload per tab inside this window. */
export const FIRESTORE_RECOVERY_WINDOW_MS = 10 * 60 * 1000;

/** sessionStorage key holding Date.now() of the last recovery reload. */
export const FIRESTORE_RECOVERY_AT_KEY = 'ldg-firestore-recovery-at';

const PROBE_KEY = 'ldg-firestore-recovery-probe';

/** Which of the two messages was seen. */
export type FirestoreClosedTrigger = 'b815' | 'connection-closing';

/**
 * What started the recovery: one of the two messages, or a Firestore proven
 * dead without a word ('stalled', lib/firestoreLiveness.ts).
 */
export type FirestoreRecoveryTrigger = FirestoreClosedTrigger | 'stalled';

/** How a silent death was proven, both on the phone itself (lib/firestoreLiveness.ts). */
export type StallProof = 'queue-restricted' | 'storage-silent';

/**
 * Where the liveness probe ran: a return to the app after a real absence, a
 * restore from the back/forward cache, a tapped save still waiting, or the
 * same save still waiting a minute in.
 */
export type FirestoreStallChannel = 'return' | 'bfcache' | 'slow-save' | 'long-save';

/** Where it was seen. */
export type FirestoreRecoveryChannel = 'firestore-log' | 'error' | 'unhandledrejection' | FirestoreStallChannel;

const ASSERTION_FAILED = /INTERNAL ASSERTION FAILED/;
const B815_ID = /\(ID: b815\)/;
const CONNECTION_CLOSING = /The database connection is closing/i;

function messageOf(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const message = (value as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return '';
}

/**
 * The narrow matcher. A string (a log line, an ErrorEvent's message) or
 * anything with a string `message` (an Error, a DOMException, a
 * FirestoreError). The b815 test needs BOTH halves, so a different assertion
 * id, or an ordinary message that happens to mention b815, never counts.
 */
export function classifyFirestoreClosed(value: unknown): FirestoreClosedTrigger | null {
  let text: string;
  try {
    text = messageOf(value);
  } catch {
    // A hostile or broken `message` getter is not our signal.
    return null;
  }
  if (!text) return null;
  if (ASSERTION_FAILED.test(text) && B815_ID.test(text)) return 'b815';
  if (CONNECTION_CLOSING.test(text)) return 'connection-closing';
  return null;
}

export type FirestoreRecoveryInput = {
  /** sessionStorage survived a write, read and remove just now. */
  storageUsable: boolean;
  /** Since this tab's last recovery reload. Infinity when there was none. */
  msSinceLastRecovery: number;
  /** Since anything last reloaded this tab (reloadGuard.ts). Infinity when nothing has. */
  msSinceLastReload: number;
  uploadInFlight: boolean;
  /** The unsaved-work registry or a non-empty textarea. */
  hasUnsavedWork: boolean;
  /** An open [role="dialog"] or <dialog open>. */
  modalOpen: boolean;
  /** document.visibilityState is not 'hidden'. */
  visible: boolean;
};

export type FirestoreRecoveryBarReason =
  | 'storage'
  | 'recent-recovery'
  | 'recent-reload'
  | 'upload'
  | 'unsaved-work'
  | 'modal'
  /**
   * The reload was asked for and did not go: a page's leave warning held it
   * (the person may have answered Stay), or it could not start. Seen only on
   * a browser that shows the warning, so never on an iPhone.
   */
  | 'reload-held';

export type FirestoreRecoveryDecision =
  | { action: 'reload' }
  | { action: 'bar'; reason: FirestoreRecoveryBarReason }
  | { action: 'wait' };

/**
 * The whole safety judgement as one pure function. 'reload' means go now.
 * 'bar' means show "The Hub lost its connection. Tap to reload." and ask
 * again at the next return to the app. 'wait' means the page is hidden and
 * nothing else stands in the way: decide when it comes back on screen.
 *
 * The loop guards come first on purpose, so a tab that cannot be guarded, or
 * that already spent its reload, says so plainly with the bar whatever else
 * is going on.
 */
export function decideFirestoreRecovery(input: FirestoreRecoveryInput): FirestoreRecoveryDecision {
  if (!input.storageUsable) return { action: 'bar', reason: 'storage' };
  if (!(input.msSinceLastRecovery >= FIRESTORE_RECOVERY_WINDOW_MS)) {
    return { action: 'bar', reason: 'recent-recovery' };
  }
  if (!(input.msSinceLastReload >= RELOAD_COOLDOWN_MS)) return { action: 'bar', reason: 'recent-reload' };
  if (input.uploadInFlight) return { action: 'bar', reason: 'upload' };
  if (input.hasUnsavedWork) return { action: 'bar', reason: 'unsaved-work' };
  if (input.modalOpen) return { action: 'bar', reason: 'modal' };
  if (!input.visible) return { action: 'wait' };
  return { action: 'reload' };
}

/** The slice of Storage this needs, so a test can hand in a plain object. */
export type RecoveryStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Proves the store works (write, read back, remove) and reads the throttle in
 * the same breath. Any throw, or a write that does not read back, is
 * `usable: false`, which the decision turns into "never reload". A missing or
 * unreadable stamp is Infinity: nothing to wait out. A stamp in the future (a
 * phone clock moved back) reads as negative, which is inside the window, so
 * a confused clock fails toward the bar.
 */
export function readRecoveryThrottle(
  getStore: () => RecoveryStore,
  now: number,
): { usable: boolean; msSinceLastRecovery: number } {
  try {
    const store = getStore();
    store.setItem(PROBE_KEY, '1');
    if (store.getItem(PROBE_KEY) !== '1') return { usable: false, msSinceLastRecovery: 0 };
    store.removeItem(PROBE_KEY);
    const raw = store.getItem(FIRESTORE_RECOVERY_AT_KEY);
    if (raw === null) return { usable: true, msSinceLastRecovery: Infinity };
    const last = Number(raw);
    if (!Number.isFinite(last)) return { usable: true, msSinceLastRecovery: Infinity };
    return { usable: true, msSinceLastRecovery: now - last };
  } catch {
    return { usable: false, msSinceLastRecovery: 0 };
  }
}

/**
 * Stamps the throttle and reads it back. False means the stamp is not really
 * there, and the caller MUST NOT reload automatically: a reload whose guard
 * was never written is a reload that can repeat forever.
 */
export function stampRecoveryThrottle(getStore: () => RecoveryStore, now: number): boolean {
  try {
    const store = getStore();
    const value = String(now);
    store.setItem(FIRESTORE_RECOVERY_AT_KEY, value);
    return store.getItem(FIRESTORE_RECOVERY_AT_KEY) === value;
  } catch {
    return false;
  }
}

/** What happened, for Sentry and for /hub-check style debugging. Never names a person. */
export type FirestoreRecoveryEvent =
  | { kind: 'detected'; trigger: FirestoreRecoveryTrigger; channel: FirestoreRecoveryChannel; proof?: StallProof }
  | { kind: 'reload'; trigger: FirestoreRecoveryTrigger; channel: FirestoreRecoveryChannel; proof?: StallProof }
  | { kind: 'bar'; trigger: FirestoreRecoveryTrigger; reason: FirestoreRecoveryBarReason; proof?: StallProof }
  | { kind: 'wait'; trigger: FirestoreRecoveryTrigger }
  | { kind: 'tap'; trigger: FirestoreRecoveryTrigger | null };

/** Everything the controller touches, handed in so a test can fake each one. */
export type FirestoreRecoveryEnv = {
  now(): number;
  isVisible(): boolean;
  uploadInFlight(): boolean;
  hasUnsavedWork(): boolean;
  modalOpen(): boolean;
  msSinceLastReload(): number;
  /** May throw (a browser that refuses storage). */
  sessionStore(): RecoveryStore;
  /** The shared reload stamp (reloadGuard.ts markReload). */
  markReload(): void;
  /**
   * Starts the reload. Returns true when a page's leave warning held it up:
   * the browser asked the person, who may answer Stay, so the page may still
   * be here when this returns. Anything else means the navigation started.
   */
  reload(): boolean | void;
  report(event: FirestoreRecoveryEvent): void;
};

export type FirestoreRecoverySnapshot = { readonly barVisible: boolean };

export type FirestoreRecoveryController = {
  /** Feed it anything that might be the fatal message. True when it matched. */
  detect(value: unknown, channel: FirestoreRecoveryChannel): boolean;
  /**
   * A Firestore proven dead without throwing (lib/firestoreLiveness.ts). The
   * same first-detection rule and the same decision as detect(). Never call
   * it on slowness alone: only a verdict of 'dead' comes here.
   */
  stalled(proof: StallProof, channel: FirestoreStallChannel): boolean;
  /**
   * A return to the app: decide again if the database closed, including
   * after a reload this page started that evidently did not go.
   */
  recheck(): void;
  /** The bar was tapped. Always reloads. */
  tapReload(): void;
  subscribe(listener: () => void): () => void;
  getSnapshot(): FirestoreRecoverySnapshot;
};

const HIDDEN_BAR: FirestoreRecoverySnapshot = Object.freeze({ barVisible: false });
const SHOWN_BAR: FirestoreRecoverySnapshot = Object.freeze({ barVisible: true });

function readBoolean(read: () => boolean, onThrow: boolean): boolean {
  try {
    return read();
  } catch {
    return onThrow;
  }
}

/**
 * One controller per page load. The FIRST matching detection decides; later
 * ones (once Firestore is dead every call throws b815, so they arrive by the
 * dozen) change nothing. After that the decision is asked again only at a
 * return to the app, through recheck().
 *
 * Every probe of the page is fenced: one that throws counts as the unsafe
 * answer (an upload in flight, unsaved work, a dialog open, hidden), so a
 * broken probe can only ever cost a bar, never a surprise reload.
 */
export function createFirestoreRecovery(env: FirestoreRecoveryEnv): FirestoreRecoveryController {
  let detected: { trigger: FirestoreRecoveryTrigger; channel: FirestoreRecoveryChannel; proof?: StallProof } | null =
    null;
  // The proof rides along only on a stall, so the two message triggers report
  // exactly what they always did.
  const withProof = (proof: StallProof | undefined) => (proof ? { proof } : {});
  let reloading = false;
  let barReported = false;
  let waitReported = false;
  let snapshot: FirestoreRecoverySnapshot = HIDDEN_BAR;
  const listeners = new Set<() => void>();

  const report = (event: FirestoreRecoveryEvent) => {
    try {
      env.report(event);
    } catch {
      // Reporting is never allowed to stop the recovery itself.
    }
  };

  const showBar = (trigger: FirestoreRecoveryTrigger, reason: FirestoreRecoveryBarReason, proof?: StallProof) => {
    if (!barReported) {
      barReported = true;
      report({ kind: 'bar', trigger, reason, ...withProof(proof) });
    }
    if (snapshot.barVisible) return;
    snapshot = SHOWN_BAR;
    for (const listener of [...listeners]) {
      try {
        listener();
      } catch {
        // One broken subscriber must not keep the bar from the others.
      }
    }
  };

  /**
   * env.reload(), with the flag that stops anything else acting while the
   * page goes. True when the navigation started. False when a leave warning
   * held it up, or the call threw: the page is still here, so the flag is
   * cleared at once rather than left set for good.
   */
  const startReload = (): boolean => {
    reloading = true;
    let held: boolean;
    try {
      held = env.reload() === true;
    } catch {
      held = true;
    }
    if (held) reloading = false;
    return !held;
  };

  const decide = () => {
    if (!detected || reloading) return;
    const { trigger, channel, proof } = detected;
    const now = env.now();
    const throttle = readRecoveryThrottle(() => env.sessionStore(), now);
    let msSinceLastReload: number;
    try {
      msSinceLastReload = env.msSinceLastReload();
    } catch {
      msSinceLastReload = 0;
    }
    const decision = decideFirestoreRecovery({
      storageUsable: throttle.usable,
      msSinceLastRecovery: throttle.msSinceLastRecovery,
      msSinceLastReload,
      uploadInFlight: readBoolean(() => env.uploadInFlight(), true),
      hasUnsavedWork: readBoolean(() => env.hasUnsavedWork(), true),
      modalOpen: readBoolean(() => env.modalOpen(), true),
      visible: readBoolean(() => env.isVisible(), false),
    });
    if (decision.action === 'reload') {
      // The stamp must be durably written before the navigation starts.
      if (!stampRecoveryThrottle(() => env.sessionStore(), now)) {
        showBar(trigger, 'storage', proof);
        return;
      }
      reloading = true;
      try {
        env.markReload();
      } catch {
        // The recovery stamp above is the guard that matters here.
      }
      // Reported before the page goes, so Sentry sees how often this fires.
      report({ kind: 'reload', trigger, channel, ...withProof(proof) });
      if (startReload()) return;
      // Held up: the person was asked and may stay on this dead page. The bar
      // now, so Stay never leaves them with nothing to tap. The stamp above
      // keeps this from turning into a second automatic reload.
      showBar(trigger, 'reload-held', proof);
      return;
    }
    if (decision.action === 'bar') {
      showBar(trigger, decision.reason, proof);
      return;
    }
    if (!waitReported) {
      waitReported = true;
      report({ kind: 'wait', trigger });
    }
  };

  return {
    detect(value, channel) {
      if (detected || reloading) return detected !== null;
      const trigger = classifyFirestoreClosed(value);
      if (!trigger) return false;
      detected = { trigger, channel };
      report({ kind: 'detected', trigger, channel });
      decide();
      return true;
    },
    stalled(proof, channel) {
      // The same first-detection rule as detect(): whichever death was seen
      // first decides, and the flood after it changes nothing.
      if (detected || reloading) return detected !== null;
      detected = { trigger: 'stalled', channel, proof };
      report({ kind: 'detected', trigger: 'stalled', channel, proof });
      decide();
      return true;
    },
    recheck() {
      // A return to the app, on a page that already started a reload, means
      // that reload did not happen (a leave warning answered with Stay where
      // the browser asked after reload() had returned, or a navigation that
      // never went), or has not landed yet. Either way this page is still
      // here and still has a dead database, so it decides again rather than
      // waiting for good. The ten minute stamp written before every reload
      // makes that decision the bar, never a second automatic reload.
      reloading = false;
      decide();
    },
    tapReload() {
      // A person chose this, so it reloads whatever the guards say. The stamp
      // is still written where it can be, so the page that comes back does
      // not spend an automatic reload straight after a manual one.
      stampRecoveryThrottle(() => env.sessionStore(), env.now());
      reloading = true;
      try {
        env.markReload();
      } catch {
        // Best effort, same as the bar's own reload in FreshnessPill.
      }
      report({ kind: 'tap', trigger: detected?.trigger ?? null });
      // Held up by a leave warning: the bar stays, so the person can tap
      // again once they have dealt with what the page was warning about.
      if (!startReload() && detected) showBar(detected.trigger, 'reload-held', detected.proof);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      return snapshot;
    },
  };
}
